import React, { useState } from "react";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import TodayEventItem from "./TodayEventItem";
import TodayEventsNavigation, { Directions } from "./TodayEventsNavigation";
import useCalendarEvents from "../hooks/useCalendarEvents";
import useLoadKlydos from "../hooks/useLoadKlydos";
import CalendarEventDialog from "./CalendarEventDialog";
import { PopulatedCalendarEvent } from "../../../api/calendarEvent";
import {
  groupEventsByCountry,
  getPrioritizedEvents,
} from "../../../utils/calendarEventPriority";
import CountrySectionHeader from "./CountrySectionHeader";
import EventItemWithPriority from "./EventItemWithPriority";

type TodayCalendarEventsListProps = {
  initialDate?: Date;
};

const TodayCalendarEventsList = ({
  initialDate = new Date(),
}: TodayCalendarEventsListProps) => {
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [selectedEvent, setSelectedEvent] =
    useState<PopulatedCalendarEvent | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const {
    data: calendarEvents,
    isLoading: eventsLoading,
    error: eventsError,
  } = useCalendarEvents({
    year: currentDate.getFullYear(),
    month: currentDate.getMonth() + 1,
    day: currentDate.getDate(),
  });

  const {
    populatedEvents,
    klydosLoading,
    klydosError,
    handleLoadKlydos,
    klydosLoaded,
  } = useLoadKlydos({
    year: currentDate.getFullYear(),
    month: currentDate.getMonth() + 1,
    day: currentDate.getDate(),
  });

  const holidays =
    populatedEvents ||
    calendarEvents?.map((event) => ({
      ...event,
      klydos: [],
    })) ||
    [];

  const handleNavigate = (direction: Directions) => {
    const newDate = new Date(currentDate);
    if (direction === Directions.PREV) {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleGoToToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateChange = (date: Date) => {
    setCurrentDate(date);
  };

  const handleEventClick = (event: PopulatedCalendarEvent) => {
    setSelectedEvent(event);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEvent(null);
  };

  if (eventsLoading) {
    return (
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (eventsError) {
    return (
      <DSBox sx={{ textAlign: "center", py: 4 }}>
        <DSTypography variant="h6" color="error" gutterBottom>
          Error loading events
        </DSTypography>
        <DSTypography variant="body2" color="text.secondary">
          {eventsError.message}
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox>
      <TodayEventsNavigation
        currentDate={currentDate}
        onNavigate={handleNavigate}
        onGoToToday={handleGoToToday}
        onDateChange={handleDateChange}
        onLoadKlydos={handleLoadKlydos}
        klydosLoading={klydosLoading}
        klydosError={klydosError}
        klydosLoaded={klydosLoaded}
      />

      {holidays && holidays.length > 0 ? (
        <DSBox>
          {Object.entries(groupEventsByCountry(holidays)).map(
            ([countryName, countryEvents]) => {
              const prioritizedEvents = getPrioritizedEvents(countryEvents);
              const isMultiplePrioritized = prioritizedEvents.length > 1;

              return (
                <DSBox
                  key={countryName}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    backgroundColor: "white.5",
                  }}
                >
                  <CountrySectionHeader
                    countryName={countryName}
                    eventCount={countryEvents.length}
                    variant="h6"
                  />

                  {countryEvents.map((event, eventIndex) => {
                    const isPrioritized = prioritizedEvents.some(
                      (e) => e.id === event.id,
                    );

                    return (
                      <DSBox
                        key={event.id}
                        sx={{
                          mb: eventIndex < countryEvents.length - 1 ? 2 : 0,
                        }}
                      >
                        <EventItemWithPriority
                          event={event}
                          isPrioritized={isPrioritized}
                          isMultiplePrioritized={isMultiplePrioritized}
                          variant="inline"
                        />
                        <TodayEventItem
                          event={event}
                          onClick={() => handleEventClick(event)}
                          date={currentDate}
                        />
                      </DSBox>
                    );
                  })}
                </DSBox>
              );
            },
          )}
        </DSBox>
      ) : (
        <DSBox
          sx={{
            textAlign: "center",
            py: 6,
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <DSTypography variant="h6" color="text.secondary" gutterBottom>
            No events found
          </DSTypography>
          <DSTypography variant="body2" color="text.secondary">
            There are no events scheduled for{" "}
            {currentDate.toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </DSTypography>
        </DSBox>
      )}

      {selectedEvent && (
        <CalendarEventDialog
          open={modalOpen}
          onClose={handleCloseModal}
          calendarEvent={selectedEvent}
        />
      )}
    </DSBox>
  );
};

export default TodayCalendarEventsList;
